import React, { useState, useEffect, lazy, Suspense } from 'react'
import { useUser } from './auth'
import './styles/App.css'
import { BrowserRouter as Router, Redirect, Route, Switch, useHistory } from 'react-router-dom'
import PublicRoute from './router/PublicRoute'
import PrivateRoute from './router/PrivateRoute'
import { AnalyticsProvider } from './analytics/AnalyticsProvider'
import SideBar from './components/SideBar'
import { Toaster } from 'react-hot-toast'
import * as Sentry from '@sentry/react'
import { getByToken, logUserTimeAsync } from './server/server'
import Loading from './components/Loading'
import { useSocket } from './context/socket'
import {
    checkIfTeacherHasPrimaryClasses,
    checkIfTeacherHasRegularClasses,
    checkIfUserNeedsTutorial,
    loadAdditionalFields,
    loadAssignedFeedbacks,
    loadUnseenConversations,
    logLoginTime,
} from './server/usersData'
import usePageVisibility from './utils/usePageVisibility'
import { useAppContext } from './context/AppContext'
import TermsAndConditionsModal from './components/modals/TermsAndConditionsModal'
import { useTranslation } from 'react-i18next'
import CustomLogoIcon from './components/common/CustomLogoIcon'
import { ContentContextProvider } from './components/content/ContentContext'
// Eagerly loaded — shown immediately on first visit
import LoginScreen from './screens/LoginScreen'
import Dashboard from './components/dashboard/Dashboard'
import NoPermission from './screens/NoPermission'
import { isItAccess } from './constants/Roles'

// Lazy-loaded route components — only downloaded when the user navigates to that route
const EarlyDashboard = lazy(() => import('./components/early/dashboard/EarlyDashboard'))
const Content = lazy(() => import('./components/content/Content'))
const ReadyToGo = lazy(() => import('./components/content/ReadyToGo'))
const CreateSurvey = lazy(() => import('./components/content/CreateSurvey'))
const ContentProgram = lazy(() => import('./components/content/ContentProgram'))
const ClassroomHome = lazy(() => import('./components/classroom/ClassroomHome'))
const EarlyClassroomHome = lazy(() => import('./components/early/classroom/EarlyClassroomHome'))
const StudentSelectEmotionScreen = lazy(() => import('./components/early/classroom/StudentSelectEmotionScreen'))
const StudentsSettingsScreen = lazy(() => import('./components/early/classroom/StudentsSettingsScreen'))
const Chat = lazy(() => import('./components/chat/Chat'))
const ManagementHome = lazy(() => import('./components/management/ManagementHome'))
const UploadEngineScreen = lazy(() => import('./components/management/upload_engine/UploadEngineScreen'))
const CatchUpHome = lazy(() => import('./components/catchUp/CatchUpHome'))
const CounselingHome = lazy(() => import('./components/counseling/CounselingHome'))
const StudentWellbeingProfile = lazy(() => import('./components/counseling/StudentWellbeingProfile'))
const Settings = lazy(() => import('./components/settings/Settings'))
const InstructionVideos = lazy(() => import('./components/videos/InstructionVideos'))
const AnalyticsHome = lazy(() => import('./components/analytics/AnalyticsHome'))
const AnalyticsSearch = lazy(() => import('./components/analytics/search/AnalyticsSearch'))
const StudentAnalyticsHome = lazy(() => import('./components/analytics/student/StudentAnalyticsHome'))
const ClassAnalyticsHome = lazy(() => import('./components/analytics/class/ClassAnalyticsHome'))
const CounsellorAnalyticsHome = lazy(() => import('./components/analytics/counsellor/CounsellorAnalyticsHome'))
const ManageCustomReports = lazy(() => import('./components/analytics/CustomReports/ManageCustomReports'))
const AnalyzeContent = lazy(() => import('./components/analytics/AnalyticsContent'))
const AlertsHome = lazy(() => import('./components/alerts/AlertsHome'))
const NotificationsHome = lazy(() => import('./components/alerts/NotificationsHome'))
const OnboardingScreen = lazy(() => import('./components/onboarding/OnboardingScreen'))

function App() {
    let history = useHistory()
    const { i18n } = useTranslation()
    const { user, setUser, isSignedIn } = useUser()
    const { socketStates, changeSocketState } = useSocket()
    const { appState, setAppState } = useAppContext()
    const [authLoading, setAuthLoading] = useState(true)
    const [showTutorial, setTutorial] = useState(false)
    const pageIsNotVisible = usePageVisibility()
    const [activeTime, setActiveTime] = useState(Date.now())
    const [sidebarAnimation, setSidebarAnimation] = useState(false)
    const searchParams = new URLSearchParams(window.location.search)
    const authToken = searchParams.get('token') ?? null
    const [isTCModalVisible, setTCModalVisible] = useState(false)

    const logout = () => {
        setUser({})
        localStorage.clear()
        setTimeout(() => {
            history?.push('/')
        }, 200)
    }

    const checkUserAuth = async () => {
        if (authToken) {
            localStorage.setItem('token', authToken)
            searchParams.delete('token')
            const newUrl = `${window.location.pathname}`
            window.history.replaceState(null, '', newUrl)
        }
        const resp = await getByToken()
        if (resp?.status === 200) {
            const data = resp.data
            const user = data
            if (user.role == 0) {
                logout()
                setAuthLoading(false)
            }
            localStorage.setItem('token', data['token'])
            setUser(user)
            // setAuthLoading(false);
        } else if (resp?.status === 406) {
            setTimeout(() => {
                logout()
                setAuthLoading(false)
            }, 1000)
        } else {
            setTimeout(() => {
                logout()
                setAuthLoading(false)
            }, 1000)
        }
    }

    useEffect(() => {
        if (isSignedIn) {
            initUserData()
            setTimeout(() => setSidebarAnimation(true), 200)
            if (!user.agree && ['en', 'de'].includes(i18n.language)) {
                setTimeout(() => setTCModalVisible(true), 700)
                // setTCModalVisible(true)
            }
        } else setSidebarAnimation(false)
    }, [isSignedIn])

    const initUserData = async () => {
        // IT staff do not have access to chat, wellbeing, tutorials, or class
        // data. Only load the additional fields needed by user management.
        if (isItAccess(user)) {
            const additionalFields = await loadAdditionalFields()
            setAppState({ additionalFields, primary: false })
            setAuthLoading(false)
            return
        }

        const unseenConversations = await loadUnseenConversations()
        const assignedFeedbacks = await loadAssignedFeedbacks()
        const showTutorial = await checkIfUserNeedsTutorial()
        if (showTutorial) {
            setTimeout(() => {
                setTutorial(true)
            }, [900])
        }
        const hasPrimaryClasses = await checkIfTeacherHasPrimaryClasses()

        const additionalFields = await loadAdditionalFields()
        const primaryUi = localStorage.getItem('uiMode') === 'primary'
        setAppState({ additionalFields, primary: primaryUi })
        changeSocketState({ ...socketStates, unseenConversations, assignedFeedbacks })
        setUser({ ...user, hasPrimaryClasses, hasRegularClasses: true, hasOnlyPrimaryStudents: false })
        if (hasPrimaryClasses) {
            const hasRegularClasses = await checkIfTeacherHasRegularClasses()

            setUser({ ...user, hasPrimaryClasses, hasRegularClasses, hasOnlyPrimaryStudents: !hasRegularClasses })
        }

        setAuthLoading(false)
    }

    useEffect(() => {
        if (user) {
            checkUserAuth()
        }
        if (window.location.pathname === '/login') {
            setAuthLoading(false)
        }
    }, [])

    useEffect(() => {
        if (isSignedIn) {
            if (!pageIsNotVisible) setActiveTime(Date.now())
            else logUserTime()
        }
    }, [pageIsNotVisible, isSignedIn])

    const logUserTime = () => {
        const data = {
            start_time: activeTime,
            end_time: Date.now(),
            device: 'desktop',
        }
        logUserTimeAsync(data)
        setActiveTime(Date.now())
        uploadLoginLogs()
    }
    const uploadLoginLogs = async () => {
        let restLogins = JSON.parse(localStorage.getItem('loginsLogs') || '[]')
        while (restLogins.length > 0) {
            const data = restLogins.pop()
            const status = await logLoginTime(data, false)
            if (!status) {
                return
            }
        }
        localStorage.setItem('loginsLogs', '[]')
    }

    useEffect(() => {
        uploadLoginLogs()
        if (isSignedIn) window.addEventListener('beforeunload', logUserTime)
        return () => {
            if (isSignedIn) window.removeEventListener('beforeunload', logUserTime)
        }
    }, [isSignedIn])

    const onDisagree = async () => {
        setTCModalVisible(false)
        localStorage.clear()
        setUser({})
    }

    const primaryUI = !!appState?.primary
    const isAdmin = user?.role == 'school_admin' || user?.role_coverage?.target_text == 'All students'
    // const isAdmin = false

    return (
        <ContentContextProvider>
            <div id="home" className="app">
                <Router>
                    <AnalyticsProvider enabled={!authLoading}>
                        {isSignedIn && !authLoading && <SideBar />}
                        {authLoading && (
                            <div className="flex justify-center items-center w-full h-screen bg-deepOcean flex-col absolute z-50">
                                <CustomLogoIcon width={350} height={150} />
                                <Loading primaryColor="#FFF" secondaryColor="#FFF" size={50} stroke={3} />
                            </div>
                        )}
                        {!authLoading && (
                            <div
                                className={`home-container ${sidebarAnimation ? 'logged-in' : ''}`}
                                onMouseEnter={(e) => {
                                    e.preventDefault()
                                }}
                                id="home-container"
                            >
                                <Suspense fallback={<div className="bg-lightBlue flex justify-center items-center w-full h-screen animate-pulseSize"></div>}>
                                    <Switch>
                                        <Route exact path="/">
                                            {isSignedIn ? <Redirect to={isItAccess(user) ? '/management' : '/dashboard'} /> : <Redirect to="/login" />}
                                        </Route>
                                        <PublicRoute path="/login" component={LoginScreen} />

                                        <PrivateRoute path="/no-permission" component={NoPermission} />

                                        <PrivateRoute path="/dashboard" component={primaryUI ? EarlyDashboard : Dashboard} />
                                        <PrivateRoute exact path="/content" component={Content} />
                                        <PrivateRoute exact path="/readytogo" component={ReadyToGo} />
                                        <PrivateRoute path="/classroom/:pageID(\\?emotion=.*)?" component={ClassroomHome} />
                                        <PrivateRoute path="/chat" component={Chat} />
                                        <PrivateRoute path="/management/upload_user_data" component={UploadEngineScreen} />
                                        <PrivateRoute path="/management" component={ManagementHome} />
                                        <PrivateRoute path="/catch-up" component={CatchUpHome} />
                                        <PrivateRoute path="/counseling/student_profile/:pageID(\\?id=.*)?" component={StudentWellbeingProfile} />
                                        <PrivateRoute path="/counseling" component={CounselingHome} />
                                        <PrivateRoute path="/settings" component={Settings} />
                                        <PrivateRoute path="/videos" component={InstructionVideos} />
                                        <PrivateRoute exact path="/content/surveys/:pageID(\\?id=.*)?" component={CreateSurvey} />
                                        <PrivateRoute path="/content/program/:pageID(\\?id=.*)?" component={ContentProgram} />

                                        <PrivateRoute path="/analytics/search" component={AnalyticsSearch} />
                                        <PrivateRoute path="/analytics/student" component={StudentAnalyticsHome} />
                                        <PrivateRoute path="/analytics/class" component={ClassAnalyticsHome} />
                                        <PrivateRoute path="/analytics/counsellor" component={CounsellorAnalyticsHome} />

                                        <PrivateRoute path="/analytics/custom-report" component={ManageCustomReports} />
                                        <PrivateRoute path="/analytics/data" component={AnalyzeContent} />
                                        <PrivateRoute path="/analytics" component={AnalyticsHome} />

                                        <PrivateRoute path="/early/dashboard" component={EarlyDashboard} />
                                        <PrivateRoute path="/early/classroom/student-select-emotion" component={StudentSelectEmotionScreen} />
                                        <PrivateRoute path="/early/classroom/student-settings" component={StudentsSettingsScreen} />
                                        <PrivateRoute path="/early/classroom" component={EarlyClassroomHome} />

                                        <PrivateRoute path="/alerts/notification" component={isAdmin ? NotificationsHome : () => <Redirect to="/alerts" />} />
                                        <PrivateRoute path="/alerts" component={AlertsHome} />
                                        <PrivateRoute path="/onboarding" component={OnboardingScreen} />

                                        <Route path="*">{isSignedIn ? <Redirect to={isItAccess(user) ? '/management' : '/dashboard'} /> : <Redirect to="/" />}</Route>
                                    </Switch>
                                </Suspense>
                            </div>
                        )}
                        <TermsAndConditionsModal closeModal={() => setTCModalVisible(false)} modalIsOpen={isTCModalVisible} onDisagree={onDisagree} />
                    </AnalyticsProvider>
                </Router>
                {/* {isSignedIn && (
                    <TutorialModal
                        modalIsOpen={false}
                        // modalIsOpen={true}
                        closeModal={() => {
                            setTutorial(false)
                        }}
                        data={CounsellorsAnalyticsSteps}
                    />
                )} */}
            </div>
            <Toaster position="top-right" reverseOrder={false} toastOptions={{ duration: 3000 }} />
        </ContentContextProvider>
    )
}

export default Sentry.withProfiler(App)
// export default App;
